import Dexie, { type Table } from 'dexie';
import type { ProjectStorage } from './storage';
import type { Class, EvalSample, NewProjectInput, Project, Sample, TrainedModel } from './types';

class BrytaDB extends Dexie {
  projects!: Table<Project, string>;
  classes!: Table<Class, string>;
  samples!: Table<Sample, string>;
  trainedModels!: Table<TrainedModel, string>;
  evalSamples!: Table<EvalSample, string>;

  constructor() {
    super('bryta-learn');
    // v1: projects only.
    this.version(1).stores({
      projects: 'id, updatedAt',
    });
    // v2: classes are added.
    this.version(2).stores({
      projects: 'id, updatedAt',
      classes: 'id, projectId, [projectId+position]',
    });
    // v3: samples per class. classId index drives the per-class queries;
    // [classId+createdAt] keeps the order stable for thumbnails.
    this.version(3).stores({
      projects: 'id, updatedAt',
      classes: 'id, projectId, [projectId+position]',
      samples: 'id, classId, [classId+createdAt]',
    });
    // v4: add a projectId index on samples so the project-delete cascade
    // can `where('projectId').equals(...)` instead of joining via classes.
    this.version(4).stores({
      projects: 'id, updatedAt',
      classes: 'id, projectId, [projectId+position]',
      samples: 'id, classId, projectId, [classId+createdAt]',
    });
    // v5: trained model metadata. Actual TF.js weights live under
    // `indexeddb://bryta-${projectId}` in TF's own IndexedDB store; this
    // table just records which projects have a trained model and when.
    this.version(5).stores({
      projects: 'id, updatedAt',
      classes: 'id, projectId, [projectId+position]',
      samples: 'id, classId, projectId, [classId+createdAt]',
      trainedModels: 'projectId, trainedAt',
    });
    // v6: held-out eval samples. Same shape as samples but a separate
    // table so they never get fed back into training. The `projectId`
    // index drives the per-project listing; `classId` drives the per-class
    // breakdown.
    this.version(6).stores({
      projects: 'id, updatedAt',
      classes: 'id, projectId, [projectId+position]',
      samples: 'id, classId, projectId, [classId+createdAt]',
      trainedModels: 'projectId, trainedAt',
      evalSamples: 'id, projectId, classId, [projectId+createdAt]',
    });
  }
}

const db = new BrytaDB();

function newProject(input: NewProjectInput): Project {
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    name: input.name,
    kind: input.kind,
    createdAt: now,
    updatedAt: now,
  };
}

function newClass(projectId: string, name: string, position: number): Class {
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    projectId,
    name,
    position,
    createdAt: now,
    updatedAt: now,
  };
}

function newSample(
  projectId: string,
  classId: string,
  blob: Blob,
  opts?: { poseKeypoints?: Sample['poseKeypoints'] },
): Sample {
  const base: Sample = {
    id: crypto.randomUUID(),
    classId,
    projectId,
    blob,
    createdAt: Date.now(),
  };
  // Only attach the field when it's actually provided so existing
  // image samples stay byte-identical to before.
  if (opts?.poseKeypoints && opts.poseKeypoints.length > 0) {
    base.poseKeypoints = opts.poseKeypoints;
  }
  return base;
}

export const dexieStorage: ProjectStorage = {
  async list() {
    return db.projects.orderBy('updatedAt').reverse().toArray();
  },

  async get(id) {
    return (await db.projects.get(id)) ?? null;
  },

  async create(input) {
    const project = newProject(input);
    await db.transaction('rw', db.projects, db.classes, async () => {
      await db.projects.add(project);
      if (input.kind === 'audio') {
        await db.classes.add(newClass(project.id, 'Background noise', 0));
      }
    });
    return project;
  },

  async rename(id, name) {
    const existing = await db.projects.get(id);
    if (!existing) {
      throw new Error(`Cannot rename: project ${id} does not exist`);
    }
    const next: Project = { ...existing, name, updatedAt: Date.now() };
    await db.projects.put(next);
    return next;
  },

  async setBlockProgram(id, program) {
    const existing = await db.projects.get(id);
    if (!existing) {
      throw new Error(`Cannot setBlockProgram: project ${id} does not exist`);
    }
    // Strip the field rather than persisting `undefined`, so projects
    // on disk stay clean under exactOptionalPropertyTypes.
    const { blockProgram: _drop, ...rest } = existing;
    void _drop;
    const next: Project =
      program === null
        ? { ...rest, updatedAt: Date.now() }
        : { ...rest, blockProgram: program, updatedAt: Date.now() };
    await db.projects.put(next);
    return next;
  },

  async setPublishConfig(id, config) {
    const existing = await db.projects.get(id);
    if (!existing) {
      throw new Error(`Cannot setPublishConfig: project ${id} does not exist`);
    }
    const { publishConfig: _drop, ...rest } = existing;
    void _drop;
    const next: Project =
      config === null
        ? { ...rest, updatedAt: Date.now() }
        : { ...rest, publishConfig: config, updatedAt: Date.now() };
    await db.projects.put(next);
    return next;
  },

  async setServerId(id, serverId) {
    const existing = await db.projects.get(id);
    if (!existing) {
      throw new Error(`Cannot setServerId: project ${id} does not exist`);
    }
    const next: Project = { ...existing, serverId, updatedAt: Date.now() };
    await db.projects.put(next);
    return next;
  },

  async markRemixed(id) {
    const existing = await db.projects.get(id);
    if (!existing) {
      throw new Error(`Cannot markRemixed: project ${id} does not exist`);
    }
    const stamp = Date.now();
    const next: Project = { ...existing, remixedAt: stamp, updatedAt: stamp };
    await db.projects.put(next);
    return next;
  },

  async delete(id) {
    await db.transaction(
      'rw',
      [db.projects, db.classes, db.samples, db.trainedModels, db.evalSamples],
      async () => {
        await db.samples.where('projectId').equals(id).delete();
        await db.classes.where('projectId').equals(id).delete();
        await db.trainedModels.where('projectId').equals(id).delete();
        await db.evalSamples.where('projectId').equals(id).delete();
        await db.projects.delete(id);
      },
    );
  },

  async listClasses(projectId) {
    return db.classes.where('projectId').equals(projectId).sortBy('position');
  },

  async createClass(projectId, name) {
    return await db.transaction('rw', db.classes, async () => {
      const existing = await db.classes.where('projectId').equals(projectId).count();
      const cls = newClass(projectId, name, existing);
      await db.classes.add(cls);
      return cls;
    });
  },

  async renameClass(id, name) {
    const existing = await db.classes.get(id);
    if (!existing) {
      throw new Error(`Cannot rename: class ${id} does not exist`);
    }
    const next: Class = { ...existing, name, updatedAt: Date.now() };
    await db.classes.put(next);
    return next;
  },

  async setClassCaptureSettings(id, settings) {
    const existing = await db.classes.get(id);
    if (!existing) {
      throw new Error(`Cannot update settings: class ${id} does not exist`);
    }
    const cleaned = Object.fromEntries(Object.entries(settings).filter(([, v]) => v !== undefined));
    const next: Class =
      Object.keys(cleaned).length > 0
        ? { ...existing, captureSettings: cleaned, updatedAt: Date.now() }
        : (() => {
            const { captureSettings: _drop, ...rest } = existing;
            return { ...rest, updatedAt: Date.now() };
          })();
    await db.classes.put(next);
    return next;
  },

  async setClassDisabled(id, disabled) {
    const existing = await db.classes.get(id);
    if (!existing) {
      throw new Error(`Cannot toggle disabled: class ${id} does not exist`);
    }
    const next: Class = disabled
      ? { ...existing, disabled: true, updatedAt: Date.now() }
      : (() => {
          const { disabled: _drop, ...rest } = existing;
          return { ...rest, updatedAt: Date.now() };
        })();
    await db.classes.put(next);
    return next;
  },

  async deleteClass(id) {
    await db.transaction('rw', db.classes, db.samples, async () => {
      await db.samples.where('classId').equals(id).delete();
      await db.classes.delete(id);
    });
  },

  async listSamples(classId) {
    return db.samples.where('classId').equals(classId).sortBy('createdAt');
  },

  async countSamples(classId) {
    return db.samples.where('classId').equals(classId).count();
  },

  async addSample(classId, blob, opts) {
    return await db.transaction('rw', db.classes, db.samples, async () => {
      const cls = await db.classes.get(classId);
      if (!cls) {
        throw new Error(`Cannot add sample: class ${classId} does not exist`);
      }
      const sample = newSample(cls.projectId, classId, blob, opts);
      await db.samples.add(sample);
      return sample;
    });
  },

  async reassignSample(id, newClassId) {
    return await db.transaction('rw', db.classes, db.samples, async () => {
      const existing = await db.samples.get(id);
      if (!existing) {
        throw new Error(`Cannot reassign: sample ${id} does not exist`);
      }
      const target = await db.classes.get(newClassId);
      if (!target) {
        throw new Error(`Cannot reassign: class ${newClassId} does not exist`);
      }
      if (target.projectId !== existing.projectId) {
        throw new Error('Cannot reassign a sample to a class in a different project');
      }
      const next: Sample = { ...existing, classId: newClassId };
      await db.samples.put(next);
      return next;
    });
  },

  async deleteSample(id) {
    await db.samples.delete(id);
  },

  async getTrainedModel(projectId) {
    return (await db.trainedModels.get(projectId)) ?? null;
  },

  async saveTrainedModel(model) {
    await db.trainedModels.put(model);
  },

  async deleteTrainedModel(projectId) {
    await db.trainedModels.delete(projectId);
  },

  async listEvalSamples(projectId) {
    return db.evalSamples.where('projectId').equals(projectId).sortBy('createdAt');
  },

  async addEvalSample(projectId, classId, blob) {
    return await db.transaction('rw', db.classes, db.evalSamples, async () => {
      const cls = await db.classes.get(classId);
      if (!cls || cls.projectId !== projectId) {
        throw new Error(`Cannot add eval sample: class ${classId} does not exist in this project`);
      }
      const sample: EvalSample = {
        id: crypto.randomUUID(),
        projectId,
        classId,
        blob,
        createdAt: Date.now(),
      };
      await db.evalSamples.add(sample);
      return sample;
    });
  },

  async deleteEvalSample(id) {
    await db.evalSamples.delete(id);
  },
};

// Exported for tests that want to wipe persisted state in a real browser.
export async function _resetDexieStorage(): Promise<void> {
  await db.transaction(
    'rw',
    [db.projects, db.classes, db.samples, db.trainedModels, db.evalSamples],
    async () => {
      await db.projects.clear();
      await db.classes.clear();
      await db.samples.clear();
      await db.trainedModels.clear();
      await db.evalSamples.clear();
    },
  );
}
